import type { ReactNode } from 'react';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import { useWatch } from 'react-hook-form';
import type {
  FormDescriptor,
  FormFieldDescriptor,
  ActionDescriptor,
  FormSubmissionContext,
  FormSubmissionResult,
  FormErrorDescriptor,
} from '@finance/contracts';
import type { ActionExecutionContext } from '../../runtime/actions/action-executor';
import { FormActionBar } from './forms/FormActionBar.tsx';
import { FormErrorSummary } from './FormErrorSummary.tsx';
import { FormHeader } from './FormHeader.tsx';
import { FormLayout } from './FormLayout.tsx';
import { useFormRuntime } from '../../runtime/forms/use-form-runtime';
import '../../runtime/forms/register-collection-adapter';
import '../../runtime/forms/register-percentage-adapter';
import '../../runtime/forms/register-position-transfer-lots-adapter';
import '../../runtime/forms/register-journal-workflow-payloads';
import { useI18nDebugContext } from '../../i18n/use-i18n-debug-context';
import { useUnsavedChangesGuard } from '../../hooks/use-unsaved-changes';
import { useAppI18n } from '../../i18n/use-app-i18n';

export interface FormRendererProps {
  contract: FormDescriptor;
  onSubmit?: (
    commandId: string,
    values: Record<string, unknown>,
    context: FormSubmissionContext
  ) => Promise<FormSubmissionResult>;
  onAction?: (action: ActionDescriptor, context?: ActionExecutionContext) => void | Promise<void>;
  serverErrors?: FormErrorDescriptor[] | Record<string, string> | null;
  pending?: boolean;
  initialValues?: Record<string, unknown>;
  loadingInitialValues?: boolean;
  recordVersion?: number;
  /** Full-page form shells already provide the page title/description. */
  showHeader?: boolean;
  /** Optional presentation adjacent to a resolved field; never changes authority. */
  renderFieldSupplement?: (
    field: FormFieldDescriptor,
    formValues: Record<string, unknown>
  ) => ReactNode;
  /** Refetches the authoritative record after an explicit conflict recovery choice. */
  onConflictReload?: () => Promise<void>;
}

export function FormRenderer({
  contract,
  onSubmit,
  onAction,
  serverErrors,
  pending = false,
  initialValues,
  loadingInitialValues,
  recordVersion,
  showHeader = true,
  renderFieldSupplement,
  onConflictReload,
}: FormRendererProps) {
  useI18nDebugContext(String(contract.id));
  const { text } = useAppI18n();
  const runtime = useFormRuntime({
    contract,
    onSubmit,
    onAction,
    pending,
    serverErrors,
    initialValues,
    loadingInitialValues,
    recordVersion,
  });

  const {
    control,
    register,
    formState,
    getValues,
    submitAction,
    handleAction,
    visibleFields,
    fieldStates,
    formValues,
    visibleActions,
    resolvedSections,
    isDirty,
    submissionState,
    submissionErrors,
    clearConflict,
  } = runtime;

  useWatch({ control });

  const dirtyGuard = useUnsavedChangesGuard(isDirty && submissionState !== 'pending');

  const serverErrorRecord = Object.fromEntries(
    Object.entries(submissionErrors.fieldErrors).map(([field, error]) => [
      field,
      String(error.code),
    ])
  );
  const effectivePending = pending || submissionState === 'pending';
  const canReloadConflict = Boolean(
    submissionErrors.conflict && contract.submission.conflictReload && onConflictReload
  );

  if (loadingInitialValues) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}
        role="status"
        aria-live="polite"
        aria-label={text('common.loading')}
      >
        <CircularProgress aria-hidden="true" />
      </Box>
    );
  }

  return (
    <>
      {dirtyGuard}
      <Box
        component="form"
        className="form-renderer"
        data-testid="form-renderer"
        aria-label={text(contract.titleKey)}
        noValidate
        onSubmit={event => {
          event.preventDefault();
          const submitActionEntry = visibleActions.find(action => action.isFormSubmit);
          if (submitActionEntry) void submitAction(submitActionEntry.action.id);
        }}
      >
        {showHeader && <FormHeader descriptor={contract} />}
        <FormErrorSummary
          descriptor={contract}
          fieldErrors={formState.errors}
          globalErrors={submissionErrors.globalErrors}
          submissionError={submissionErrors.submissionError}
          focusOnRender={submissionState === 'failed'}
        />
        {canReloadConflict && (
          <Alert severity="warning" sx={{ mb: 2 }} role="status">
            <Button
              type="button"
              onClick={() => void onConflictReload?.().then(clearConflict)}
              autoFocus
            >
              {text(contract.submission.conflictReload!.labelKey)}
            </Button>
          </Alert>
        )}

        {submissionState === 'succeeded' && contract.submission.successMessageKey && (
          <Alert
            severity="success"
            aria-live="polite"
            sx={{ mb: 2 }}
            data-testid="form-submission-success"
          >
            {text(contract.submission.successMessageKey)}
          </Alert>
        )}

        <FormLayout
          descriptor={contract}
          visibleFields={visibleFields}
          fieldStates={fieldStates}
          resolvedSections={resolvedSections}
          register={register}
          control={control}
          formState={formState}
          getValues={getValues}
          serverErrors={serverErrorRecord}
          pending={effectivePending}
          formValues={formValues}
          renderFieldSupplement={renderFieldSupplement}
        />

        <FormActionBar
          actions={visibleActions}
          onAction={handleAction}
          pending={effectivePending}
          isDirty={isDirty}
          submissionState={submissionState}
        />
      </Box>
    </>
  );
}
